
// eslint-disable-next-line @tseslint/no-shadow
import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, InputSignal, model, ModelSignal, Output, Signal, OnInit } from '@angular/core';
import { NotificationComponent } from "../notification/notification.component";
import { NotificationHeaderComponent } from "../notification/components/notification-header/notification-header.component";
import { NotificationContentComponent } from "../notification/components/notification-content/notification-content.component";
import { NotificationFooterComponent } from "../notification/components/notification-footer/notification-footer.component";
import { Process, PROCESS_STATUS, ProcessNotification } from '@core/types/process.types';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButton } from "@angular/material/button";
import { NgTemplateOutlet } from '@angular/common';
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faCalendar, faDownload, faPause, faRefresh, faTrashCan, faXmark } from '@fortawesome/pro-light-svg-icons';
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { toObservable } from '@angular/core/rxjs-interop';
import { TemplatePipeModule } from '@ogs-gmbh/ngx-template-engine';
import { differenceInCalendarDays } from 'date-fns';
import { DateTimeComponent } from '@shared/components/date-time/date-time.component';
import { InlineConfirmationMessageComponent } from "@shared/components/inline-confirmation/components/inline-confirmation-message/inline-confirmation-message.component";
import { InlineConfirmationComponent } from "@shared/components/inline-confirmation/inline-confirmation.component";
import { InlineConfirmationOperatorsComponent } from "@shared/components/inline-confirmation/components/inline-confirmation-operators/inline-confirmation-operators.component";
import { InlineConfirmationTriggerDirective } from "@shared/components/inline-confirmation/directives/inline-confirmation-trigger.directive";
import { InlineConfirmationConfirmDirective } from "@shared/components/inline-confirmation/directives/inline-confirmation-confirm.directive";
import { InlineConfirmationCancelDirective } from "@shared/components/inline-confirmation/directives/inline-confirmation-cancel.directive";
import { ProcessNotificationIsSeenChange } from './types/process-notification-is-seen-change.type';

@Component({
  selector: 'app-process-notification',
  standalone: true,
  imports: [ NotificationComponent,
    NotificationHeaderComponent,
    NotificationContentComponent,
    NotificationFooterComponent,
    MatProgressSpinnerModule,
    MatButton,
    IconModule,
    TranslationPipeModule,
    TemplatePipeModule,
    DateTimeComponent,
    InlineConfirmationMessageComponent,
    InlineConfirmationComponent,
    InlineConfirmationOperatorsComponent,
    NgTemplateOutlet,
    InlineConfirmationTriggerDirective,
    InlineConfirmationConfirmDirective,
    InlineConfirmationCancelDirective ],
  providers: [ provideTranslationScope("core/process-notification") ],
  templateUrl: './process-notification.component.html',
  styleUrl: './process-notification.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProcessNotificationComponent implements OnInit {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  public readonly processNotification: InputSignal<ProcessNotification> = input.required();

  protected readonly _isSeen: ModelSignal<boolean> = model(true);

  protected readonly isDeleteConfirmationOpen: ModelSignal<boolean> = model(false);

  @Output() public readonly deleteClick: EventEmitter<Process> = new EventEmitter<Process>();

  @Output() public readonly pauseClick: EventEmitter<Process> = new EventEmitter<Process>();

  @Output() public readonly restartClick: EventEmitter<Process> = new EventEmitter<Process>();

  @Output() public readonly downloadClick: EventEmitter<Process> = new EventEmitter<Process>();

  @Output() public readonly isSeenChange: EventEmitter<ProcessNotificationIsSeenChange> = new EventEmitter<ProcessNotificationIsSeenChange>();

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  public isNotificationReadable: Signal<boolean> = computed(() =>
    this.processNotification().process.status === PROCESS_STATUS.readyToDownload
    && !this.isDeleteConfirmationOpen());

  protected readonly PROCESS_STATUS: typeof PROCESS_STATUS = PROCESS_STATUS;

  // eslint-disable-next-line @unicorn/consistent-function-scoping
  protected remainingDaysForDownloadParams: Signal<number[]> = computed(() => [ differenceInCalendarDays(this.processNotification().process.validUntil ?? new Date(), new Date()) ]);

  constructor () {
    this._faIconLibrary.addIcons(faCalendar, faDownload, faXmark, faTrashCan, faPause, faRefresh);
    toObservable(this.processNotification).subscribe({
      next: (notification: ProcessNotification) => {
        this.setSeenStatus(notification);
      }
    });
  }

  public ngOnInit (): void {
    this.setSeenStatus(this.processNotification());
  }

  private setSeenStatus (processNotification: ProcessNotification): void {
    const isSeen: boolean = this.isNotificationReadable() ? processNotification.isSeen : true;

    if (this._isSeen() !== isSeen)
      this._isSeen.set(isSeen);
  }

  protected toggleDeleteConfirmation (): void {
    const newValue: boolean = !this.isDeleteConfirmationOpen();

    this.isDeleteConfirmationOpen.set(newValue);
  }

  protected downloadProcess (): void {
    this.downloadClick.emit(this.processNotification().process);
  }

  protected restartProcess (): void {
    this.restartClick.emit(this.processNotification().process);
  }

  protected _changeIsSeen (isSeen: boolean): void {
    this.isSeenChange.emit({ processId: this.processNotification().process.id, isSeen });
  }

  protected deleteProcess (): void {
    this.deleteClick.emit(this.processNotification().process);
  }

  protected pauseProcess (): void {
    this.pauseClick.emit(this.processNotification().process);
  }
}
